// ============================================================
// ANSICHTSSACHE-AGENT – Der Hinweis auf die App
// ============================================================
// Ein Angebot, kein Aufsteller: er kommt einmal, er laesst sich
// wegklicken, und danach kommt er nicht wieder. Wer ihn
// weggeklickt hat und es sich anders ueberlegt, findet die
// Installation unter „Mein Konto -> App“ — ein Hinweis, der
// nach dem Schliessen ins Leere fuehrte, waere eine Sackgasse.
//
// Er liegt ueber dem Inhalt und schiebt ihn nicht.
// Listenseiten rechnen ihre Hoehe aus Bildschirm minus
// Kopfzeile; ein Band im Fluss haette jede von ihnen um seine
// eigene Hoehe ueberlaufen lassen.
//
// Gezeigt wird er nur, wenn hier auch wirklich etwas zu tun
// ist: nicht im schon installierten Fenster, und nicht in einem
// Browser, der die Installation gar nicht kennt.
//
// Er steht zweimal: in der `AppShell` und schon auf der
// Anmeldung. Dort trifft er jeden beim ersten Besuch, und wer
// gleich installiert, meldet sich nur einmal an — in der App.
// Weggeklickt gilt fuer beide Stellen, der Merker ist derselbe.
// ============================================================

"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/ui-store";
import { ANLEITUNG, useInstallation } from "./installation";

export function InstallationsHinweis({
  showMoreLink = true,
  className,
}: {
  /** Vor der Anmeldung aus: „Mein Konto -> App“ fuehrte dort zurueck auf die Anmeldung. */
  showMoreLink?: boolean;
  /** Andere Lage, wo unten in der Mitte schon etwas steht (Anmeldekarte). */
  className?: string;
} = {}) {
  const { installiert, weg, installieren } = useInstallation();
  const gesehen = useUIStore((zustand) => zustand.installationshinweisGesehen);
  const schliessen = useUIStore((zustand) => zustand.installationshinweisSchliessen);

  if (gesehen || installiert || weg === "keiner") return null;

  const anleitung = ANLEITUNG[weg];

  return (
    <aside
      aria-label="Hinweis zur Installation"
      // Unten in der Mitte und nicht in einer Ecke: rechts unten
      // sitzen die Meldungen (Sonner), links unten die eigene
      // Person in der Leiste. Beides waere verdeckt. Unter `lg`
      // steht die Chat-Blase tief in der Ecke — der Hinweis steht
      // dann ueber ihr, sonst laege sie auf seinen Knoepfen.
      className={cn(
        "animate-slide-up fixed right-4 bottom-4 left-4 z-40 max-lg:bottom-[calc(var(--ans-chatblase)+3.75rem)] sm:right-auto sm:left-1/2 sm:w-[22rem] sm:-translate-x-1/2",
        className,
      )}
    >
      <div className="bg-bg-elevated border-border shadow-e3 rounded-xl border p-4">
        <p className="text-body-sm text-text-primary font-medium">Der Agent als App</p>
        <p className="text-caption text-text-secondary mt-1.5">
          Ablegen wie ein Programm: eigenes Symbol, eigenes Fenster, keine Adresszeile. Dieselbe
          Anwendung, nur ohne Browser darum herum.
        </p>
        {anleitung && <p className="text-caption text-text-secondary mt-1.5">{anleitung}</p>}

        <div className="mt-4 flex items-center gap-2">
          {/* Beim Angebot zwei Wege — installieren oder lassen.
              Bei der Anleitung nur einer: „Nicht jetzt“ taete
              daneben exakt dasselbe wie „Verstanden“, und zwei
              Knoepfe fuer eine Handlung sind einer zu viel. */}
          {weg === "angebot" ? (
            <>
              <Button
                size="md"
                onClick={async () => {
                  await installieren();
                  // Gefragt ist gefragt: ob die Person zugestimmt
                  // hat oder nicht, ein zweites Mal fragt der
                  // Hinweis nicht.
                  schliessen();
                }}
              >
                Installieren
              </Button>
              <Button variant="ghost" size="md" onClick={schliessen}>
                Nicht jetzt
              </Button>
            </>
          ) : (
            <Button size="md" onClick={schliessen}>
              Verstanden
            </Button>
          )}
          {showMoreLink && (
            <Link
              href="/mein-konto/app"
              onClick={schliessen}
              className="text-caption text-text-tertiary hover:text-text-secondary ml-auto underline-offset-4 hover:underline"
            >
              Mehr dazu
            </Link>
          )}
        </div>
      </div>
    </aside>
  );
}
